import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, XCircleIcon } from 'lucide-react';
import { BankTheme } from '../../constants/bankThemes';
export interface FaceIDScreenProps {
  bankName: string;
  onAuthenticated: () => void;
  onFailed?: () => void;
  bankTheme?: BankTheme;
}
export function FaceIDScreen({
  bankName,
  onAuthenticated,
  onFailed,
  bankTheme
}: FaceIDScreenProps) {
  const [status, setStatus] = useState<'scanning' | 'success' | 'failed'>('scanning');
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    // Simulate Face ID scanning
    const scanDuration = 2000; // 2 seconds
    const interval = 50;
    const steps = scanDuration / interval;
    const increment = 100 / steps;
    const timer = setInterval(() => {
      setProgress(prev => {
        const next = prev + increment;
        if (next >= 100) {
          clearInterval(timer);
          // Success after scanning completes
          setTimeout(() => {
            setStatus('success');
            setTimeout(() => {
              onAuthenticated();
            }, 800);
          }, 200);
          return 100;
        }
        return next;
      });
    }, interval);
    return () => clearInterval(timer);
  }, [onAuthenticated]);
  const primaryColor = bankTheme?.colors.primary || '#00BCD4';
  const primaryGradient = bankTheme?.gradients.button || 'linear-gradient(135deg, #00BCD4 0%, #1976D2 100%)';
  return <div className="min-h-screen flex flex-col items-center justify-center p-6 bg-gradient-to-br from-gray-50 to-gray-100" data-id="element-813">
      <div className="w-full max-w-md" data-id="element-814">
        {/* Bank Logo */}
        <motion.div initial={{
        opacity: 0,
        y: -20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="text-center mb-12" data-id="element-815">
          <div className="text-sm font-medium text-gray-600 mb-2" data-id="element-816">
            Authenticating with
          </div>
          <div className="text-xl font-bold" style={{
          color: primaryColor
        }} data-id="element-817">
            {bankName}
          </div>
        </motion.div>

        {/* Face ID Animation Container */}
        <motion.div initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 0.3
      }} className="relative w-64 h-64 mx-auto mb-8" data-id="element-818">
          {/* Outer glow ring */}
          <motion.div className="absolute inset-0 rounded-full" style={{
          background: `radial-gradient(circle, ${primaryColor}20 0%, transparent 70%)`
        }} animate={status === 'scanning' ? {
          scale: [1, 1.1, 1],
          opacity: [0.3, 0.6, 0.3]
        } : {}} transition={{
          duration: 2,
          repeat: status === 'scanning' ? Infinity : 0,
          ease: 'easeInOut'
        }} data-id="element-819" />

          {/* Face outline with scanning effect */}
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-820">
            {/* Face outline */}
            <motion.path d="M100 30 C130 30, 150 50, 150 80 L150 120 C150 150, 130 170, 100 170 C70 170, 50 150, 50 120 L50 80 C50 50, 70 30, 100 30 Z" stroke={status === 'success' ? '#10B981' : status === 'failed' ? '#EF4444' : primaryColor} strokeWidth="3" strokeLinecap="round" fill="none" initial={{
            pathLength: 0
          }} animate={{
            pathLength: 1
          }} transition={{
            duration: 0.8,
            ease: 'easeInOut'
          }} data-id="element-821" />

            {/* Scanning lines */}
            <AnimatePresence data-id="element-822">
              {status === 'scanning' && <>
                  {[0, 1, 2, 3, 4].map(i => <motion.line key={i} x1="50" y1={30 + i * 35} x2="150" y2={30 + i * 35} stroke={primaryColor} strokeWidth="2" strokeOpacity="0.6" initial={{
                pathLength: 0,
                opacity: 0
              }} animate={{
                pathLength: [0, 1, 0],
                opacity: [0, 0.8, 0]
              }} transition={{
                duration: 1.5,
                repeat: Infinity,
                delay: i * 0.15,
                ease: 'easeInOut'
              }} data-id="element-823" />)}
                </>}
            </AnimatePresence>

            {/* Face features */}
            {/* Eyes */}
            <motion.circle cx="75" cy="85" r="4" fill={status === 'success' ? '#10B981' : status === 'failed' ? '#EF4444' : primaryColor} initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.3,
            duration: 0.3
          }} data-id="element-824" />
            <motion.circle cx="125" cy="85" r="4" fill={status === 'success' ? '#10B981' : status === 'failed' ? '#EF4444' : primaryColor} initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            delay: 0.3,
            duration: 0.3
          }} data-id="element-825" />

            {/* Nose */}
            <motion.path d="M100 95 L100 110" stroke={status === 'success' ? '#10B981' : status === 'failed' ? '#EF4444' : primaryColor} strokeWidth="2" strokeLinecap="round" initial={{
            pathLength: 0
          }} animate={{
            pathLength: 1
          }} transition={{
            delay: 0.4,
            duration: 0.3
          }} data-id="element-826" />

            {/* Mouth */}
            <motion.path d="M85 130 Q100 140, 115 130" stroke={status === 'success' ? '#10B981' : status === 'failed' ? '#EF4444' : primaryColor} strokeWidth="2" strokeLinecap="round" fill="none" initial={{
            pathLength: 0
          }} animate={{
            pathLength: 1
          }} transition={{
            delay: 0.5,
            duration: 0.3
          }} data-id="element-827" />
          </svg>

          {/* Success/Failure overlay */}
          <AnimatePresence data-id="element-828">
            {status === 'success' && <motion.div initial={{
            scale: 0,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} exit={{
            scale: 0,
            opacity: 0
          }} className="absolute inset-0 flex items-center justify-center" data-id="element-829">
                <div className="w-20 h-20 rounded-full bg-green-500 flex items-center justify-center shadow-lg" data-id="element-830">
                  <CheckCircle2Icon className="w-12 h-12 text-white" strokeWidth={3} data-id="element-831" />
                </div>
              </motion.div>}
            {status === 'failed' && <motion.div initial={{
            scale: 0,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} exit={{
            scale: 0,
            opacity: 0
          }} className="absolute inset-0 flex items-center justify-center" data-id="element-832">
                <div className="w-20 h-20 rounded-full bg-red-500 flex items-center justify-center shadow-lg" data-id="element-833">
                  <XCircleIcon className="w-12 h-12 text-white" strokeWidth={3} data-id="element-834" />
                </div>
              </motion.div>}
          </AnimatePresence>
        </motion.div>

        {/* Status text */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.3
      }} className="text-center" data-id="element-835">
          <AnimatePresence mode="wait" data-id="element-836">
            {status === 'scanning' && <motion.div key="scanning" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} data-id="element-837">
                <div className="text-xl font-semibold text-gray-900 mb-2" data-id="element-838">
                  Authenticate with Face ID
                </div>
                <div className="text-sm text-gray-600" data-id="element-839">
                  Position your face within the frame
                </div>
              </motion.div>}
            {status === 'success' && <motion.div key="success" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} data-id="element-840">
                <div className="text-xl font-semibold text-green-600 mb-2" data-id="element-841">
                  Authentication Successful
                </div>
                <div className="text-sm text-gray-600" data-id="element-842">
                  Proceeding to account selection...
                </div>
              </motion.div>}
            {status === 'failed' && <motion.div key="failed" initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} data-id="element-843">
                <div className="text-xl font-semibold text-red-600 mb-2" data-id="element-844">
                  Authentication Failed
                </div>
                <div className="text-sm text-gray-600 mb-4" data-id="element-845">
                  Face ID not recognized
                </div>
                <button onClick={() => {
              setStatus('scanning');
              setProgress(0);
            }} className="px-6 py-2 rounded-full text-sm font-medium text-white" style={{
              background: primaryGradient
            }} data-id="element-846">
                  Try Again
                </button>
              </motion.div>}
          </AnimatePresence>
        </motion.div>

        {/* Progress indicator */}
        {status === 'scanning' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.5
      }} className="mt-8" data-id="element-847">
            <div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden" data-id="element-848">
              <motion.div className="h-full rounded-full" style={{
            background: primaryGradient,
            width: `${progress}%`
          }} transition={{
            duration: 0.1
          }} data-id="element-849" />
            </div>
          </motion.div>}

        {/* Alternative option */}
        {status === 'scanning' && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.8
      }} className="text-center mt-8" data-id="element-850">
            <button className="text-sm font-medium" style={{
          color: primaryColor
        }} data-id="element-851">
              Use Passcode Instead
            </button>
          </motion.div>}
      </div>
    </div>;
}